import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';

import { SUB_COMPONENTS } from 'components/Globals/Analytics/constants';

import { TP } from 'constants/index';

import { useTranslation } from 'src/i18n';

import { TextButton } from '../LinkButton';

import classes from './CollapsedContent.module.scss';

const CollapsedContent = ({
  children,
  maxHeightPx = 300,
  defaultCollapsed = true,
  allowToggle = false,
  collapseThresholdPx = 50,
  labelCollapseAction,
  labelExpandAction,
  trackingData = {},
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isContentLong, setIsContentLong] = useState(false);
  const contentRef = useRef(null);

  /* NOTE: Logic Explanation
     If the content height is less than or equal to maxHeightPx + collapseThresholdPx,
     the content is never collapsed and the toggle button is never shown,
     regardless of allowToggle or defaultCollapsed.
     Only allow toggling if the content is longer than maxHeightPx + collapseThresholdPx.
  */
  useLayoutEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;

      // NOTE: Only collapse if content is more than maxHeightPx + collapseThresholdPx
      if (contentHeight <= maxHeightPx + collapseThresholdPx) {
        setIsCollapsed(false);
        setIsContentLong(false);
      } else {
        setIsCollapsed(contentRef.current.userInteracted ? isCollapsed : defaultCollapsed);
        setIsContentLong(true);
      }
    }
    // NOTE: Only run on mount and when children or maxHeightPx change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, maxHeightPx, defaultCollapsed, collapseThresholdPx]);

  const toggleCollapsed = () => {
    setIsCollapsed(prev => {
      if (contentRef.current) {
        contentRef.current.userInteracted = true;
      }

      return !prev;
    });
  };

  /* NOTE: Show toggle button only if content is long enough
     If allowToggle is true, allow both expand and collapse; if false, only allow expand
  */
  const showToggleButton = isContentLong && (allowToggle || (!allowToggle && isCollapsed));

  const icon = <SpriteIcon icon={isCollapsed ? 'chevron_down' : 'chevron_up'} size={18} />;

  const actionLabel = useMemo(() => {
    if (isCollapsed) {
      return labelExpandAction || t(`${TP}.FN_CLICK_TO_SEE_MORE`);
    }

    return labelCollapseAction || t(`${TP}.FN_CLICK_TO_SEE_LESS`);
  }, [isCollapsed, labelCollapseAction, labelExpandAction, t]);

  return (
    <div className={classes.root} style={{ maxHeight: isCollapsed ? `${maxHeightPx}px` : 'none' }}>
      <div ref={contentRef}>{children}</div>
      {showToggleButton && (
        <div
          className={classnames(classes.footer, {
            [classes.footerExpanded]: allowToggle && !isCollapsed,
          })}
        >
          <div className={classes.toggleButtonWrapper}>
            <TextButton
              onClick={toggleCollapsed}
              leftIcon={icon}
              rightIcon={icon}
              trackingData={{
                ...trackingData,
                subComponent: SUB_COMPONENTS.VIEW_ALL_CTA,
              }}
            >
              {actionLabel}
            </TextButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsedContent;
